// A pull request's track (logic/track.ts), from its timeline: base, then
// its commits on the main lane, force-pushes breaking it, and the commits
// they replaced on side lanes, completed by the replaced-commits lookup
// (`REPLACED_COMMITS`) where the timeline alone cannot tell.
import type { ReplacedCommitsResult, TimelineItem } from '../api/queries';
import type { PrTab } from '../router';
import type { MainPoint, ReplacedPoint, Track, TrackCommit } from './track';

/** Commit metadata from GitHub (timeline items or the replaced-commits lookup). */
export interface CommitMeta {
  oid: string;
  abbreviatedOid: string;
  messageHeadline: string;
  committedDate: string;
  author: { name: string | null; user: { login: string } | null } | null;
}

/**
 * Result of the replaced-commits lookup (`REPLACED_COMMITS`): what the
 * timeline alone cannot tell.
 */
export interface ReplacedLookup {
  /** The pull request's current commits, or null when there are too many to list. */
  current: Set<string> | null;
  /** A force-push's `beforeCommit` → its history, newest first (itself first). */
  history: Map<string, CommitMeta[]>;
  /** Recent commits of the base branch: a history walk skips them. */
  base: Set<string>;
}

/** Most force-pushes (the latest) whose replaced commits are looked up. */
export const MAX_LOOKUP_PUSHES = 10;

/** The before sides of head force-pushes to look up (latest `MAX_LOOKUP_PUSHES`, deduplicated). */
export function forcePushBefores(items: TimelineItem[]): string[] {
  const out: string[] = [];
  for (const it of items)
    if (it.__typename === 'HeadRefForcePushedEvent' && it.beforeCommit && !out.includes(it.beforeCommit.oid)) out.push(it.beforeCommit.oid);
  return out.slice(-MAX_LOOKUP_PUSHES);
}

/** The lookup's answer for `befores` (in the query's `bN` order). */
export function parseReplaced(res: ReplacedCommitsResult, befores: string[]): ReplacedLookup {
  const repo = res.repository;
  const commits = repo?.pullRequest?.commits;
  const history = new Map<string, CommitMeta[]>();
  befores.forEach((b, i) => {
    const nodes = repo?.[`b${i}`]?.history?.nodes;
    if (nodes?.length) history.set(b, nodes);
  });
  return {
    current: commits && commits.totalCount <= commits.nodes.length ? new Set(commits.nodes.map((n) => n.commit.oid)) : null,
    history,
    base: new Set(repo?.base?.history?.nodes.map((n) => n.oid) ?? []),
  };
}

/**
 * Whether a pull request's view shows its track, so the replaced-commits
 * lookup should complete it: Snapshots always, Files while its range
 * timeline shows (`timeline`); the Overview and Review never.
 */
export function showsTrack(tab: PrTab, timeline: boolean): boolean {
  return tab === 'commits' || (tab === 'files' && timeline);
}

/** Most commits a force-push's history walk adds to the replaced lane. */
export const MAX_REPLACED_PER_PUSH = 10;

export interface PullTrackOpts {
  headRefOid: string;
  baseRefOid: string;
  baseRefName: string;
  viewer: string;
  lookup?: ReplacedLookup | null;
}

type CommitItem = Extract<TimelineItem, { __typename: 'PullRequestCommit' }>;
type ForcePush = Extract<TimelineItem, { __typename: 'HeadRefForcePushedEvent' | 'BaseRefForcePushedEvent' }>;
type Review = Extract<TimelineItem, { __typename: 'PullRequestReview' }>;

function authorOf(c: CommitMeta): string | null {
  return c.author?.user?.login ?? c.author?.name ?? null;
}

/** The viewer's last submitted review that names a commit. */
export function lastViewerReview(items: TimelineItem[], viewer: string): Review | null {
  if (!viewer) return null;
  let last: Review | null = null;
  for (const it of items) {
    if (it.__typename !== 'PullRequestReview' || it.state === 'PENDING' || !it.reviewCommit) continue;
    if (it.author?.login !== viewer) continue;
    const at = it.submittedAt ?? it.createdAt;
    if (!last || at >= (last.submittedAt ?? last.createdAt)) last = it;
  }
  return last;
}

/**
 * The pull request's track: base, then its commits in timeline order on
 * the main lane. Each head force-push breaks the main lane; the commits it
 * replaced (no longer in the pull request) fork off the main point before
 * the break: the `beforeCommit`, and, when the lookup has it, the rest of
 * its history down to commits still in the pull request or on the base
 * branch (at most `MAX_REPLACED_PER_PUSH`). Without the lookup, a commit
 * counts as replaced when a later force-push names it as `beforeCommit`.
 * A viewer's review on a commit the timeline no longer lists is placed as
 * a replaced commit where the review was made.
 */
export function pullTrack(items: TimelineItem[], opts: PullTrackOpts): Track {
  const { headRefOid, lookup } = opts;
  const commitAt = new Map<string, number>();
  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit' && !commitAt.has(it.commit.oid)) commitAt.set(it.commit.oid, i);
  });
  // Last force-push naming each SHA as its before side.
  const replacedAt = new Map<string, number>();
  items.forEach((it, i) => {
    if (it.__typename === 'HeadRefForcePushedEvent' && it.beforeCommit) replacedAt.set(it.beforeCommit.oid, i);
  });
  // In the pull request now: the head; per the lookup when it listed every
  // commit; else a timeline commit no later force-push replaced.
  const isCurrent = (oid: string): boolean => {
    if (oid === headRefOid) return true;
    if (lookup?.current) return lookup.current.has(oid);
    const at = commitAt.get(oid);
    if (at === undefined) return false;
    const r = replacedAt.get(oid);
    return r === undefined || r < at;
  };

  const base = { sha: opts.baseRefOid, name: opts.baseRefName, replaced: [] as ReplacedPoint[] };
  const main: (MainPoint & { replaced: ReplacedPoint[] })[] = [];
  /** Where a replaced commit goes: the side lane of the last main point (the base's before the first). */
  const lane = () => (main.length ? main[main.length - 1].replaced : base.replaced);
  const placed = new Set<string>();
  let pendingBreak = false;
  const review = lastViewerReview(items, opts.viewer);
  const pushedBy = new Map<string, string | null>();

  const addMain = (c: TrackCommit) => {
    placed.add(c.sha);
    main.push({ ...c, breakBefore: pendingBreak, forcePushedBy: null, replaced: [] });
    pendingBreak = false;
  };
  const addReplaced = (c: TrackCommit, by: string | null) => {
    placed.add(c.sha);
    lane().push({ ...c, replacedBy: by });
  };
  const fromMeta = (c: CommitMeta): TrackCommit => ({
    sha: c.oid,
    short: c.abbreviatedOid,
    message: c.messageHeadline,
    author: authorOf(c),
    at: c.committedDate,
  });
  const bare = (sha: string, short = sha.slice(0, 7)): TrackCommit => ({ sha, short, message: null, author: null, at: null });

  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit') {
      const c = (it as CommitItem).commit;
      if (placed.has(c.oid) || commitAt.get(c.oid) !== i) return;
      if (isCurrent(c.oid)) addMain(fromMeta(c));
      else {
        const by = items[replacedAt.get(c.oid) ?? -1] as ForcePush | undefined;
        addReplaced(fromMeta(c), by?.actor?.login ?? null);
      }
    } else if (it.__typename === 'HeadRefForcePushedEvent') {
      const fp = it as ForcePush;
      const by = fp.actor?.login ?? null;
      if (fp.afterCommit) pushedBy.set(fp.afterCommit.oid, by);
      const before = fp.beforeCommit;
      if (before) {
        const walk = lookup?.history.get(before.oid);
        const stop = (oid: string) => isCurrent(oid) || oid === opts.baseRefOid || !!lookup?.base.has(oid);
        const group: CommitMeta[] = [];
        if (walk?.length) {
          for (const c of walk) {
            if (group.length >= MAX_REPLACED_PER_PUSH) break;
            if (stop(c.oid) || placed.has(c.oid)) continue;
            group.push(c);
          }
          group.reverse();
          for (const c of group) addReplaced(fromMeta(c), by);
        } else if (!placed.has(before.oid) && !isCurrent(before.oid)) {
          addReplaced(bare(before.oid, before.abbreviatedOid), by);
        }
      }
      pendingBreak = true;
    } else if (it.__typename === 'PullRequestReview' && it === review) {
      // A review on a commit the timeline no longer lists: show it where the review was made.
      const oid = review.reviewCommit!.oid;
      if (!placed.has(oid) && !commitAt.has(oid) && !isCurrent(oid)) addReplaced(bare(oid), null);
    }
  });
  if (headRefOid && !placed.has(headRefOid)) addMain(bare(headRefOid));

  for (const p of main) if (pushedBy.has(p.sha)) p.forcePushedBy = pushedBy.get(p.sha) ?? 'someone';
  return {
    base,
    main,
    head: headRefOid,
    unheld: [],
    hasOlder: false,
    lastPush: lastPushBefore(items, headRefOid),
    lastReview: review?.reviewCommit?.oid ?? null,
  };
}

/**
 * The previous head when the head came from a force-push (that push's
 * `beforeCommit`), else null: GitHub does not report plain pushes, so
 * each commit counts as one push and "Last push" starts at the commit
 * before the head.
 */
function lastPushBefore(items: TimelineItem[], head: string): string | null {
  let lastFp: ForcePush | null = null;
  for (const it of items) if (it.__typename === 'HeadRefForcePushedEvent') lastFp = it as ForcePush;
  // The head is still what the last force-push left: it started from that push's before side.
  return lastFp?.afterCommit?.oid === head && lastFp.beforeCommit ? lastFp.beforeCommit.oid : null;
}
